/**
 * Aggiorna dal vivo il prezzo mostrato accanto al titolo del prodotto
 * (prezzo unitario x quantità selezionata), sia nella pagina prodotto
 * sia nella finestra "Aggiungi rapidamente" (quick add) aperta dalle
 * card prodotto (es. "Potrebbe interessarti anche"). Ascolta gli
 * eventi nativi del tema `quantity-selector:update` e `variant:update`
 * a livello di documento (non dipende da uno script per-prodotto, così
 * funziona anche nel contenuto della quick-add che viene iniettato
 * dinamicamente dopo il caricamento della pagina).
 *
 * Importante (bug corretto): il prezzo "unitario" (base) viene
 * catturato dal DOM UNA SOLA VOLTA per ogni ambito (all'apertura o al
 * cambio di variante) e tenuto in cache — non viene mai riletto dal
 * testo già moltiplicato. Inoltre, le scritture che questo script fa
 * sul DOM (per mostrare il totale) vengono attivamente escluse dal
 * MutationObserver che rileva l'apertura di un nuovo prodotto nella
 * quick-add: altrimenti ogni aggiornamento di prezzo fatto da questo
 * stesso script veniva scambiato per "un nuovo prodotto" e il prezzo
 * già moltiplicato veniva ricatturato come se fosse il prezzo unitario,
 * facendolo solo salire e mai scendere.
 */
(function () {
  function parseMoney(text) {
    if (!text) return null;
    const cleaned = text.replace(/[^0-9.,]/g, '');
    let normalized = cleaned;
    if (cleaned.indexOf(',') !== -1) {
      // Formato italiano/europeo: "." separatore delle migliaia, "," decimali
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    }
    const value = parseFloat(normalized);
    return isNaN(value) ? null : value;
  }

  function formatMoney(value) {
    return '€' + value.toFixed(2).replace('.', ',');
  }

  // Prezzo unitario "vero" catturato dal DOM, in cache per ambito (la
  // sezione prodotto sulla pagina, oppure la finestra quick-add).
  const baseByScope = new WeakMap();
  // Ultima quantità osservata per ciascun ambito.
  const quantityByScope = new WeakMap();
  // Observer che rileva l'apertura di un nuovo prodotto nella quick-add
  // (assegnato in watchQuickAdd). render() lo usa per scartare le
  // mutazioni che genera lui stesso, per evitare il loop di auto-innesco
  // descritto sopra.
  let quickAddObserver = null;

  function getScope(el) {
    if (!(el instanceof Element)) return document;
    return el.closest('dialog, .shopify-section') || document;
  }

  function findPriceContainer(scope) {
    return scope.querySelector('product-price[data-product-id]');
  }

  function findQuantityInput(scope) {
    return scope.querySelector('quantity-selector-component input[name="quantity"], input[name="quantity"]');
  }

  function readQuantity(scope) {
    const input = findQuantityInput(scope);
    if (input && input.value) {
      const value = parseInt(input.value, 10);
      if (!isNaN(value) && value > 0) return value;
    }
    return 1;
  }

  function captureBase(container) {
    const saleEl = container.querySelector('.price__sale .price-item--sale.price');
    const compareEl = container.querySelector('.price__sale .price-item--regular.compare-at-price');
    const regularEl = container.querySelector('.price__regular .price');

    return {
      saleEl: saleEl,
      compareEl: compareEl,
      regularEl: regularEl,
      baseSale: saleEl ? parseMoney(saleEl.textContent) : null,
      baseCompare: compareEl ? parseMoney(compareEl.textContent) : null,
      baseRegular: regularEl ? parseMoney(regularEl.textContent) : null,
    };
  }

  function render(base, quantity) {
    const qty = quantity > 0 ? quantity : 1;

    if (base.saleEl && base.baseSale != null) {
      base.saleEl.textContent = formatMoney(base.baseSale * qty);
    }
    if (base.compareEl && base.baseCompare != null) {
      base.compareEl.textContent = formatMoney(base.baseCompare * qty);
    }
    if (base.regularEl && base.baseRegular != null) {
      base.regularEl.textContent = formatMoney(base.baseRegular * qty);
    }

    // Le righe sopra hanno appena generato mutazioni DOM (assegnare
    // .textContent sostituisce il nodo di testo, quindi è una mutazione
    // "childList", non solo un cambio di testo): le scartiamo subito,
    // prima che vengano consegnate al MutationObserver della quick-add,
    // altrimenti verrebbero scambiate per "è stato aperto un nuovo
    // prodotto" e il prezzo già moltiplicato verrebbe ricatturato come
    // se fosse il prezzo unitario di partenza.
    if (quickAddObserver) {
      quickAddObserver.takeRecords();
    }
  }

  /**
   * Ricattura il prezzo unitario dal DOM e lo mette in cache per l'ambito.
   * Da chiamare SOLO quando il DOM mostra sicuramente il prezzo unitario
   * "pulito" (non ancora moltiplicato): al primo caricamento/apertura
   * quick-add, oppure subito dopo un variant:update (che riscrive il
   * markup del prezzo con il prezzo unitario della nuova variante).
   */
  function recaptureBase(scope) {
    const container = findPriceContainer(scope);
    if (!container) return null;
    const base = captureBase(container);
    baseByScope.set(scope, base);
    return base;
  }

  function initScope(scope) {
    const base = recaptureBase(scope);
    if (!base) return;
    const qty = readQuantity(scope);
    quantityByScope.set(scope, qty);
    if (qty !== 1) {
      render(base, qty);
    }
  }

  document.addEventListener('quantity-selector:update', function (event) {
    const scope = getScope(event.target instanceof Element ? event.target : null);
    const detail = event.detail || {};
    const quantity = typeof detail.quantity === 'number' ? detail.quantity : readQuantity(scope);
    quantityByScope.set(scope, quantity);

    // Riusa il prezzo unitario già in cache: NON va mai riletto dal DOM
    // qui, perché a questo punto il DOM mostra già il totale della
    // quantità precedente, non il prezzo unitario.
    let base = baseByScope.get(scope);
    if (!base) {
      base = recaptureBase(scope);
    }
    if (!base) return;
    render(base, quantity);
  });

  document.addEventListener('variant:update', function (event) {
    const scope = getScope(event.target instanceof Element ? event.target : null);
    const quantity = quantityByScope.has(scope) ? quantityByScope.get(scope) : readQuantity(scope);
    // product-price.js aggiorna il markup del prezzo con il prezzo
    // unitario della nuova variante quando riceve questo stesso evento;
    // il rAF lascia a quell'aggiornamento il tempo di completarsi prima
    // di ricatturare il nuovo prezzo unitario "pulito" dal DOM.
    requestAnimationFrame(function () {
      const base = recaptureBase(scope);
      if (!base) return;
      render(base, quantity);
    });
  });

  // Rileva quando il contenuto della finestra quick-add viene sostituito
  // (apertura di un nuovo prodotto), per catturare subito il prezzo
  // unitario del nuovo prodotto e applicare la quantità corrente.
  function watchQuickAdd() {
    const quickAddContent = document.getElementById('quick-add-modal-content');
    if (!quickAddContent || !window.MutationObserver) return;

    quickAddObserver = new MutationObserver(function () {
      const dialogEl = quickAddContent.closest('dialog') || quickAddContent;
      initScope(dialogEl);
    });
    quickAddObserver.observe(quickAddContent, { childList: true, subtree: true });
  }

  function boot() {
    document.querySelectorAll('.shopify-section, dialog').forEach(initScope);
    watchQuickAdd();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
